import { Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import UnpublishDialogIcon from "@entrilia/shared/icons/UnpublishDialogIcon";
import { logError } from "@entrilia/shared/logging";
import { dataSubmissionStatusCaptionsMap } from "@entrilia/shared/utilities/enumCaptions";
import adminApi from "../../../../api/adminApi";
import type { DataCollectionRequestDetails } from "../../../../api/types/dataCollectionTypes";

interface Props {
  dataRequestId: string;
  submissionIds: string[];
  recipientNames: string[];
  open: boolean;
  onClose: () => void;
  onApply: (dataRequest: DataCollectionRequestDetails) => void;
}

const unpublishDataRequestSubmissions = withErrorHandling(adminApi.unpublishDataRequestSubmissions);

const UnpublishSubmissionsDialog = ({
  dataRequestId,
  submissionIds,
  recipientNames,
  open,
  onClose,
  onApply,
}: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [isSaving, setIsSaving] = useState(false);

  const handleApply = async () => {
    setIsSaving(true);
    const [dataRequest, error] = await unpublishDataRequestSubmissions(dataRequestId, { submissionIds });
    setIsSaving(false);

    if (error) {
      logError(error, "[UnpublishSubmissionsDialog] unpublishDataRequestSubmissions");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("Unpublished successfully");
    onApply(dataRequest);
  };

  const prompt =
    recipientNames.length > 1
      ? `Unpublish ${recipientNames.length} submissions`
      : `Unpublish submission for ${recipientNames[0]}`;

  const description = `The ${submissionIds.length > 1 ? "submissions" : "submission"} will be returned to ${dataSubmissionStatusCaptionsMap["Draft"]} and will not be available for viewing by users`;

  return (
    <SmallDialog open={open} onClose={onClose}>
      <DialogTitle>Unpublish</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <UnpublishDialogIcon sx={{ height: 80, width: 80 }} />
          <Stack spacing={1} sx={{ alignItems: "center" }}>
            <Typography variant="h6" sx={{ textAlign: "center" }}>
              {prompt}
            </Typography>
            <Typography color="textSecondary" sx={{ textAlign: "center" }}>
              {description}
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button variant="contained" loading={isSaving} onClick={handleApply}>
          Unpublish
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default UnpublishSubmissionsDialog;
